import { SUPPORTED_LANGUAGE_CODES } from '../constants/Languages';
import { StorePaymentMethod } from '../constants/StorePaymentMethod';
import { roundMoney } from './stockQuantityUtils';
import { DEFAULT_VAT_CLASSIFICATION, toVatPayload } from './vatClassification';

const MAX_LINES = 100;

export const validateStockPurchase = (form, todayKey) => {
  const errors = {};
  const lines = Array.isArray(form?.lines) ? form.lines : [];
  const productIds = new Set();

  if (lines.length < 1 || lines.length > MAX_LINES) {
    errors.lines = 'stock.validation.linesRequired';
  }

  lines.forEach((line, index) => {
    const isNewProduct = !line?.productId;
    if (isNewProduct && !String(line?.newProductName || '').trim()) {
      errors[`line-${index}-product`] = 'stock.validation.productRequired';
    }

    if (line?.productId) {
      if (productIds.has(line.productId)) {
        errors.lines = 'stock.validation.duplicateProduct';
      }
      productIds.add(line.productId);
    }

    const quantity = Number(line?.packageQuantity);
    if (!Number.isFinite(quantity) || quantity <= 0) {
      errors[`line-${index}-quantity`] = 'stock.validation.quantityRequired';
    }

    const cost = Number(line?.unitCost);
    if (!Number.isFinite(cost) || cost < 0) {
      errors[`line-${index}-cost`] = 'stock.validation.costRequired';
    }

    const vatRate = line?.vatRate === '' || line?.vatRate == null ? Number.NaN : Number(line.vatRate);
    if (!Number.isFinite(vatRate) || vatRate < 0 || vatRate > 100) {
      errors[`line-${index}-vat`] = 'stock.validation.vatRateRequired';
    }

    if (isNewProduct) {
      const content = Number(line?.contentQuantity);
      if (!Number.isFinite(content) || content <= 0) {
        errors[`line-${index}-content`] = 'productsManagement.validation.contentRequired';
      }
      if (line?.isRetail !== false) {
        const price = Number(line?.salePrice);
        if (!Number.isFinite(price) || price <= 0) {
          errors[`line-${index}-price`] = 'productsManagement.validation.priceRequired';
        }
      }
    }
  });

  if (!form?.purchaseDate) {
    errors.purchaseDate = 'stock.validation.dateRequired';
  } else if (todayKey && form.purchaseDate > todayKey) {
    errors.purchaseDate = 'stock.validation.dateInFuture';
  }

  if (!form?.paymentMethod || form.paymentMethod === StorePaymentMethod.Online) {
    errors.paymentMethod = 'stock.validation.paymentMethodRequired';
  }

  return errors;
};

const buildLocalizedName = (name) => {
  const trimmed = String(name || '').trim();
  return Object.fromEntries(
    SUPPORTED_LANGUAGE_CODES.map((language) => [language.toUpperCase(), trimmed]),
  );
};

export const buildStockPurchasePayload = (form) => ({
  supplierId: form.supplierId || null,
  newSupplierName: form.supplierId ? null : (String(form.newSupplierName || '').trim() || null),
  invoiceReference: String(form.invoiceReference || '').trim() || null,
  purchaseDate: form.purchaseDate,
  paymentMethod: form.paymentMethod,
  notes: String(form.notes || '').trim() || null,
  idempotencyKey: form.idempotencyKey,
  lines: (form.lines || []).map((line) => {
    const productId = line.productId || null;
    const isRetail = line.isRetail !== false;
    return {
      productId,
      packageQuantity: Number(line.packageQuantity),
      unitCost: roundMoney(line.unitCost) ?? 0,
      vatRate: Number(line.vatRate),
      newProduct: productId ? null : {
        name: buildLocalizedName(line.newProductName),
        salePrice: isRetail ? Number(line.salePrice) : null,
        isRetail,
        stockUnit: line.stockUnit || 'Unit',
        contentQuantity: Number(line.contentQuantity) || 1,
        disabled: false,
        ...toVatPayload(DEFAULT_VAT_CLASSIFICATION),
      },
    };
  }),
});

export const purchaseLineTotal = (line) => {
  const quantity = Number(line?.packageQuantity) || 0;
  const cost = Number(line?.unitCost) || 0;
  return Math.round((quantity * cost) * 100) / 100;
};

export const purchaseTotal = (lines) => (
  (lines || []).reduce((sum, line) => sum + purchaseLineTotal(line), 0)
);
